import yaml from 'js-yaml';

import { type Chapter } from '@editor2/types/chapter';
import { type Entity } from '@editor2/types/entity';
import { type Page } from '@editor2/types/page';
import { type MCChoice, type MCQuestion, type PageElement } from '@editor2/types/pageElements';
import { type PageGroup } from '@editor2/types/pageGroup';

export const sanitizeAttributeName = (attribute: string) => {
	return attribute.slice(0, 1).toUpperCase() + attribute.slice(1).replace(/_/g, ' ');
};

export const getChapterTitle = (chapter: Chapter) => {
	const number = chapter.chapter_number;
	return 'Chapter' + (number && number !== ' ' ? ` ${number}. ` : ': ') + `${chapter.name}`;
};

export const getPageGroupTitle = (pageGroup: PageGroup) => {
	return `Page Group: ${pageGroup.name}`;
};

export const getPageTitle = (page: Page) => {
	const number = page.page_number;
	return 'Page' + (number && number !== ' ' ? ` ${number}. ` : ': ') + `${page.name}`;
};

export const getChoiceTitle = (choice: MCChoice) => {
	return `Choice: ${choice.body}`;
};

export const getPoolQuestionTitle = (question: MCQuestion) => {
	return `Question: ${question.body}`;
};

export const getEntityById = <T extends Chapter | PageGroup | Page | PageElement>(
	item: Entity<T>,
	id: number
) => {
	return Object.values(item.entities).find((element: T) => {
		if (element.id) return element.id === id;
	});
};

export const getParentEntityByPageId = <T extends Chapter | PageGroup>(
	item: Entity<T>,
	pageId: string
) => {
	return Object.values(item.entities).find((element: T) => element.pages?.includes(pageId));
};

export const getChapterByPageGroupId = (item: Entity<Chapter>, pageGroupId: string) => {
	return Object.values(item.entities).find((element: Chapter) =>
		element.page_groups?.includes(pageGroupId)
	);
};

export const getChapterByPageId = (
	chapters: Entity<Chapter>,
	pageGroups: Entity<PageGroup>,
	pageId: string
) => {
	return Object.values(chapters.entities).find((element: Chapter) => {
		if (element.page_groups_enabled) {
			return element.page_groups.some((pageGroupId) => {
				return pageGroups.entities[pageGroupId].pages?.includes(pageId);
			});
		} else {
			return element.pages?.includes(pageId);
		}
	});
};

export const getPageByElementId = (item: Entity<Page>, elementId: string) => {
	return Object.values(item.entities).find((element: Page) =>
		element.elements?.includes(elementId)
	);
};

export const getInitialIndex = (initialIds: string[], familyId: string) => {
	return initialIds.findIndex((id) => id === familyId);
};

// Parse the chapter label from the book YAML config, fallback to 'Chapter'.
export function parseChapterLabel(yamlConfig: string): string {
	try {
		const parsed = yaml.load(yamlConfig) as { [key: string]: unknown } | null;
		const chapters = parsed?.chapters as { [key: string]: unknown } | undefined;
		const humanName = chapters?.human_name;
		if (typeof humanName === 'string' && humanName.length > 0) {
			return humanName.charAt(0).toUpperCase() + humanName.slice(1);
		}
	} catch {
		// Fall through to default
	}
	return 'Chapter';
}

// Build a family_id → label map for human-readable associated content display.
export function buildFamilyIdLabels({ chapters, pages, yamlConfig }) {
	const map = new Map<string, string>();
	const chapterLabel = parseChapterLabel(yamlConfig);

	if (chapters) {
		for (const id of chapters.ids) {
			const ch = chapters.entities[id];
			if (!ch) continue;
			const fid = ch.family_id ?? String(ch.id ?? id);
			const num = ch.chapter_number?.trim();
			const prefix = num ? `${chapterLabel} ${num}: ` : `${chapterLabel}: `;
			map.set(fid, `${prefix}${ch.name}`);
		}
	}
	if (pages) {
		for (const id of pages.ids) {
			const pg = pages.entities[id];
			if (!pg) continue;
			const fid = pg.family_id ?? String(pg.id ?? id);
			const prefix =
				pg.page_number != null && pg.page_number !== '' ? `Page ${pg.page_number} ` : 'Page ';
			map.set(fid, `${prefix}${pg.name}`);
		}
	}
	return map;
}
